import React, { useMemo } from 'react';
import { GitBranch, CircleAlert, MousePointerClick } from 'lucide-react';

const PAGE_TYPE_COLORS = {
  'Homepage': '#60a5fa',
  'Service/Product': '#a78bfa',
  'Blog/Resource': '#34d399',
  'Contact/Conversion': '#f472b6',
  'About/Trust': '#fbbf24',
  'Other': '#94a3b8',
};

const ANCHOR_COLORS = {
  exact_match: '#f87171',
  partial_match: '#fbbf24',
  branded: '#34d399',
  generic: '#60a5fa',
  url: '#94a3b8',
};

const LinkGraphPanel = ({ project }) => {
  const graphData = project?.link_analysis?.graph;
  const anchorData = project?.link_analysis?.anchor_text;

  const graph = useMemo(() => graphData || { nodes: [], edges: [], orphan_pages: [] }, [graphData]);
  const anchor = useMemo(() => anchorData || null, [anchorData]);

  // Simple layered layout: x by depth, y by index within the layer.
  const layout = useMemo(() => {
    if (!graph.nodes.length) return { positions: {}, size: { w: 0, h: 0 } };
    const byDepth = {};
    graph.nodes.forEach((n) => {
      const d = n.depth || 0;
      byDepth[d] = byDepth[d] || [];
      byDepth[d].push(n);
    });
    const depths = Object.keys(byDepth).map(Number).sort((a, b) => a - b);
    const positions = {};
    const layerGap = 210;
    const nodeGap = 64;
    let w = depths.length * layerGap + 80;
    let h = 320;
    depths.forEach((d, di) => {
      const layer = byDepth[d];
      const x = 60 + di * layerGap;
      layer.forEach((node, ni) => {
        const y = 60 + ni * nodeGap;
        positions[node.id] = { x, y };
        h = Math.max(h, y + 70);
      });
    });
    return { positions, size: { w, h } };
  }, [graph]);

  const distribution = anchor?.distribution || {};
  const percentages = anchor?.percentages || {};
  const totalAnchors = anchor?.total_anchors || 0;
  const buckets = [
    { key: 'exact_match', label: 'Exact Match' },
    { key: 'partial_match', label: 'Partial Match' },
    { key: 'branded', label: 'Branded' },
    { key: 'generic', label: 'Generic' },
    { key: 'url', label: 'URL' },
  ];

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-300">
      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Architecture Nodes</span>
          <span className="text-xl font-mono font-black text-blue-400">{graph.total_nodes ?? 0}</span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Internal Edges</span>
          <span className="text-xl font-mono font-black text-indigo-400">{graph.total_edges ?? 0}</span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Orphan Pages</span>
          <span className={`text-xl font-mono font-black ${(graph.orphan_pages?.length || 0) > 0 ? 'text-red-400' : 'text-green-400'}`}>
            {graph.orphan_pages?.length || 0}
          </span>
        </div>
        <div className="bg-black/40 border border-gray-800/80 rounded-xl p-4 flex flex-col gap-1">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Anchor Text Score</span>
          <span className={`text-xl font-mono font-black ${(anchor?.anchor_score || 100) >= 80 ? 'text-green-400' : (anchor?.anchor_score || 0) >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
            {anchor?.anchor_score ?? 100}/100
          </span>
        </div>
      </div>

      {/* Visual graph */}
      <div className="bg-black/35 border border-gray-850 rounded-2xl p-5 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
            <GitBranch size={15} className="text-blue-400" />
            Visual Site Architecture & Internal Link Graph
          </h4>
          <span className="text-[10px] text-gray-500 font-mono">
            {graph.nodes?.length || 0} nodes · {graph.edges?.length || 0} links · PageRank flow left → right
          </span>
        </div>

        {!graph.nodes?.length ? (
          <div className="text-center py-10 text-gray-600 italic text-xs">
            Link graph unavailable — re-run an audit to map the internal link topology.
          </div>
        ) : (
          <div className="overflow-auto custom-scrollbar max-h-[420px]">
            <svg width={layout.size.w} height={layout.size.h} className="min-w-full">
              {/* Edges */}
              {graph.edges.map((e, i) => {
                const s = layout.positions[e.source];
                const t = layout.positions[e.target];
                if (!s || !t) return null;
                const midX = (s.x + t.x) / 2;
                return (
                  <path
                    key={`e-${i}`}
                    d={`M ${s.x} ${s.y} C ${midX} ${s.y}, ${midX} ${t.y}, ${t.x} ${t.y}`}
                    stroke="#334155"
                    strokeWidth="1.2"
                    fill="none"
                    className="opacity-70"
                  />
                );
              })}
              {/* Nodes */}
              {graph.nodes.map((n) => {
                const pos = layout.positions[n.id];
                if (!pos) return null;
                const isOrphan = (graph.orphan_pages || []).includes(n.url);
                const color = isOrphan ? '#f87171' : (PAGE_TYPE_COLORS[n.page_type] || '#94a3b8');
                const title = n.title ? ` - ${n.title}` : '';
                return (
                  <g key={n.id} className="group">
                    {isOrphan && (
                      <circle cx={pos.x} cy={pos.y} r="16" fill="none" stroke="#f87171" strokeWidth="1" strokeDasharray="4 3" className="animate-pulse" />
                    )}
                    <circle cx={pos.x} cy={pos.y} r="9" fill={color} opacity="0.9" />
                    <circle cx={pos.x} cy={pos.y} r="4" fill="#0b1220" />
                    <text x={pos.x} y={pos.y + 28} textAnchor="middle" fill={isOrphan ? '#fca5a5' : '#94a3b8'} fontSize="10" className="font-mono">
                      {isOrphan ? '⚠ ' : ''}{n.url.split('/').filter(Boolean).slice(-1)[0] || '/'}
                    </text>
                    <title>{`${n.url}${title}\nIn-links: ${n.inlink_count} · Out-links: ${n.outlink_count}${isOrphan ? '\nORPHAN: no internal links point to this page' : ''}`}</title>
                  </g>
                );
              })}
            </svg>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mt-1 text-[10px] text-gray-400">
          {Object.entries(PAGE_TYPE_COLORS).map(([type, color]) => (
            <span key={type} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
              {type}
            </span>
          ))}
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" /> Orphan Page
          </span>
        </div>
      </div>

      {/* Orphan pages */}
      {(graph.orphan_pages?.length || 0) > 0 && (
        <div className="bg-red-950/20 border border-red-500/25 rounded-2xl p-5 flex flex-col gap-3">
          <h4 className="font-extrabold text-red-300 text-sm flex items-center gap-2">
            <CircleAlert size={15} className="text-red-400" />
            Orphan Pages — No Internal Links Point to These
          </h4>
          <div className="flex flex-col gap-1.5">
            {graph.orphan_pages.map((url, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                <a href={url} target="_blank" rel="noreferrer" className="font-mono text-red-300 hover:text-red-200 underline truncate">{url}</a>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 leading-relaxed">
            Orphan pages are invisible to crawlers via internal links and leak PageRank. Add contextual links from related
            content (blog posts, category hubs, footer) to rescue them.
          </p>
        </div>
      )}

      {/* Anchor text distribution */}
      <div className="bg-black/35 border border-gray-850 rounded-2xl p-5 flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h4 className="font-extrabold text-white text-sm flex items-center gap-2">
            <MousePointerClick size={15} className="text-purple-400" />
            Anchor Text Distribution
          </h4>
          <span className="text-[10px] text-gray-500 font-mono">{totalAnchors} anchors analyzed</span>
        </div>

        {anchor?.over_optimization_warning && (
          <div className="bg-amber-950/30 border border-amber-500/30 text-amber-200 text-xs p-3 rounded-lg leading-relaxed">
            {anchor.over_optimization_warning}
          </div>
        )}

        {totalAnchors > 0 && (
          <div className="flex flex-col gap-2.5">
            {buckets.map(({ key, label }) => {
              const pct = percentages[key] || 0;
              const count = distribution[key] || 0;
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="w-24 text-[10px] text-gray-400 font-bold shrink-0">{label}</span>
                  <div className="flex-1 h-2.5 bg-gray-950 rounded-full overflow-hidden border border-gray-850">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${pct}%`, background: ANCHOR_COLORS[key] }}
                    />
                  </div>
                  <span className="w-16 text-right text-[10px] font-mono text-gray-400 shrink-0">{pct}% ({count})</span>
                </div>
              );
            })}
          </div>
        )}

        {anchor?.samples && totalAnchors > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-1">
            {buckets.filter(({ key }) => anchor.samples[key]?.length).map(({ key, label }) => (
              <div key={key} className="flex flex-col gap-1.5">
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: ANCHOR_COLORS[key] }}>
                  {label} Samples
                </span>
                <div className="flex flex-col gap-1">
                  {anchor.samples[key].map((s, i) => (
                    <div key={i} className="bg-gray-950 border border-gray-850 rounded-lg p-2 text-[10px] flex flex-col gap-0.5">
                      <span className="text-gray-300 font-mono">"{s.anchor}"</span>
                      <span className="text-gray-600 font-mono truncate">→ {s.url}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {totalAnchors === 0 && (
          <div className="text-center py-6 text-gray-600 italic text-xs">
            No internal anchors detected to classify.
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkGraphPanel;
